import React, { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import Button from '../../components/ui/Button';
import { useCustomVerifyEmailMutation } from '../../redux/apis/endpoints/authApi';

const EmailVerification = () => {
  const history = useHistory();
  const location = useLocation();
  const [token, setToken] = useState('');
  const [error, setError] = useState(null);
  const [verified, setVerified] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // RTK Query hook for custom email verification
  const [verifyEmail, { isLoading, error: verifyError }] = useCustomVerifyEmailMutation();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update error state when RTK Query error changes
  useEffect(() => {
    if (verifyError) {
      console.log('Verification error:', verifyError);
      
      setError(
        verifyError.data?.message || 
        verifyError.data?.error?.message || 
        'Failed to verify email. The link may have expired.'
      );
    }
  }, [verifyError]);

  useEffect(() => {
    // Extract token from query params
    const params = new URLSearchParams(location.search);
    
    // Check both 'confirmation' (legacy) and 'token' (new) parameter names
    const tokenFromParams = params.get('token') || params.get('confirmation');
    
    if (tokenFromParams) {
      console.log('Verification token found in URL:', tokenFromParams);
      setToken(tokenFromParams);
      handleVerification(tokenFromParams);
    } else {
      setError('Verification token is missing. Please use the link from the email.');
    }
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerification = async (verificationToken) => {
    setError(null);

    try {
      console.log('Verifying email with token:', verificationToken);
      
      // Use our custom verification endpoint
      const result = await verifyEmail(verificationToken).unwrap();
      
      console.log('Verification result:', result);
      
      if (result && result.success) {
        setVerified(true);
        
        // Redirect to login after a delay
        setTimeout(() => {
          history.push('/login', { 
            message: 'Your email has been verified. You can now log in.' 
          });
        }, 3000);
      } else {
        // Handle unexpected success response
        setError('Verification completed with an unexpected response. Please try logging in or contact support.');
        console.warn('Unexpected verification result:', result);
      }
    } catch (err) {
      // Error is already handled by the useEffect above
      console.error('Email verification error:', err);
    }
  };

  // Check if this is a closet user from URL params
  const urlParams = new URLSearchParams(location.search);
  const isClosetUser = urlParams.get('isClosetUser');

  // Determine background style
  const backgroundStyle = isClosetUser
    ? {
        minHeight: '100vh',
        backgroundImage:
          "url('https://res.cloudinary.com/sheyou/image/upload/v1727875894/virtual_closet_5d66ccc54e.svg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    : {
        minHeight: '100vh'
      };

  return (
    <div className="gradient_limeade flex flex-col" style={backgroundStyle}>
      <div className="flex flex-grow items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8 bg-white p-8 rounded-lg shadow-md">
          <div>
            <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
              Email Verification
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {isLoading 
                ? 'Verifying your email address...' 
                : verified 
                  ? 'Your email has been verified!' 
                  : 'Confirming your email address'}
            </p>
          </div>
          
          {error && (
            <div className="rounded-md bg-red-50 p-4 mx-auto max-w-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Verification failed</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                  <div className="mt-4">
                    <Link
                      to="/auth/check-email"
                      className="rounded-md bg-brand-teal px-2 py-1.5 text-sm font-medium text-white hover:bg-brand-teal-dark focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2 mr-3"
                    >
                      Request new verification email
                    </Link>
                    <Link
                      to={`/login${isClosetUser ? `?isClosetUser=${isClosetUser}` : ''}`}
                      className="rounded-md bg-red-50 px-2 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
                    >
                      Go to login
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {verified && (
            <div className="rounded-md bg-green-50 p-4 mx-auto max-w-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Email verified successfully</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>Your email has been successfully verified. You can now log in to your account.</p>
                  </div>
                  <div className="mt-4">
                    <Link
                      to={`/login${isClosetUser ? `?isClosetUser=${isClosetUser}` : ''}`}
                      className="rounded-md bg-green-50 px-2 py-1.5 text-sm font-medium text-green-800 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2"
                    >
                      Go to login
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {isLoading && (
            <div className="flex justify-center mt-4">
              <Button
                isLoading={true}
                className="rounded-md bg-brand-teal px-3 py-2 text-sm font-semibold text-white"
                disabled={true}
              >
                Verifying...
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerification; 